import translatejs from "translate.js";

const pluralizers = {
    // Copied from  https://github.com/StephanHoyer/translate.js/tree/master/pluralize
    en: (n) => n !== 1 ? 'p' : 's',
    is: (n) => (n % 10 !== 1 || n % 100 === 11) ? 'p' : 's',
};
const defaultLang = 'en';

export default function (widget, preferredLang) {
    const i18n = {};
    const i18nDefault = i18n[defaultLang] = {};
    const _txt = translatejs({});
    // const _txt = translatejs({}, { debug:true });

    /**
     * Wrapper around txt which intercepts translations and applies specific rules for translations of forecast types.
     * @param {string} key
     */
    const txt = (key, ...args) => {
        // First try a verbatim translation. They always have precedence.
        const str = _txt(key, ...args);

        // If we didn't get a translation and it is a forecast-type string.
        if (str === key && str.startsWith('$type--')) {

            // console.log(key, '→', str);
            // Try to translate without the level specified

            // Specific height above ground or sea
            let match = str.match(/(.*)_at_([0-9]+)(m|ft)_(agl|asl)(.*)/);
            let strWithLevel = match && translateLevel(
                match[1] + match[5],
                `$level--${match[3]}_${match[4]}`,
                match[2],
            );
            if (strWithLevel) return strWithLevel

            // Specific pressure-level or model-level
            match = str.match(/(.*)_at_([0-9]+)(z|hPa)(.*)/);
            strWithLevel = match && translateLevel(
                match[1] + match[4],
                `$level--${match[3]}`,
                match[2],
            );
            if (strWithLevel) return strWithLevel

            // Specific named (non-numeric) level
            match = str.match(/(.*)_at_(surface|sea_level|depth)(.*)/);
            strWithLevel = match && translateLevel(
                match[1] + match[3],
                `$level--${match[2]}`,
                // no level value
            );
            if (strWithLevel) return strWithLevel

            // Try a more generic key by chopping of a .foo suffix
            const idxDot = str.lastIndexOf('.');
            if (idxDot > 0) {
                const keyShort = key.substring(0, idxDot);
                // console.log(key, '→', keyShort, '→', strShort);
                const strShort = txt(keyShort, ...args);
                if (strShort !== keyShort) return strShort;
            }
        }
        return str;
    }

    /**
     * Attempt to translate variable and level separately and return the joint string
     * @param varKey
     * @param levelKey Translation key for the level such as '$level--m_agl', '$level--hPa' or '$level--surface'
     * @returns {string|*}
     */
    const translateLevel = (varKey, levelKey, levelVal) => {
        // console.log('Matches level pattern:', varKey, levelKey, levelVal);
        const strWithoutLevel = txt(varKey);
        // console.log(varKey, '→', strWithoutLevel)
        if (strWithoutLevel !== varKey) {
            levelVal = translateNumber(levelVal);
            const levelStr = _txt(levelKey, levelVal);  // What about floats?
            // console.log(levelKey, levelVal, '→', levelStr);
            let result = strWithoutLevel
            if (levelStr !== levelKey) result += (' ' + levelStr);
            // console.log('  →', result);
            return result;
        }
    }

    /**
     * Add thousands separators to numbers.
     * Chops digits off non-integers (for now). */
    const translateNumber = (val) => {
        const n = parseInt(val);
        if (!Number.isFinite(n) || (n > -1000 && n < 1000)) {
            // val is not a number or too small for separators
            return val
        }
        let s = Math.abs(n).toString();
        // console.log(val, '→', n, '→', s);
        const sign = n < 0 ? '-' : '';
        const parts = [sign + s.substr(0, s.length % 3)];
        for (let i = s.length % 3; i < s.length; i += 3) {
            parts.push(s.substr(i, 3));
        }
        const sep = _txt('$number--thousands');
        return parts.join(sep);
    }

    let desiredLangs = (navigator.languages || [])
        .map(ds => ds.toLowerCase());  // Also converts to mutable array
    desiredLangs.push(defaultLang);
    if (widget.env.lang) {
        desiredLangs.unshift(widget.env.lang);
    }
    if (preferredLang && preferredLang !== 'auto') {
        desiredLangs.unshift(preferredLang.toLowerCase());
    }
    desiredLangs = desiredLangs
        .map(s => s.replace(/-.*/, ''))  // Strip en-gb to en and pt-br to pt
        .filter((lang, pos, arr) => arr.indexOf(lang) === pos);  // Dedupe
    //console.log('Desired languages:', desiredLangs);

    const _setTranslationKeys = function () {
        let lang;
        let i = 0;
        while (!lang && i < desiredLangs.length) {
            let candiateLang = desiredLangs[i];
            if (!i18n[candiateLang] && candiateLang.length > 2) {
                candiateLang = candiateLang.substr(0, 2);
            }
            if (i18n[candiateLang] && i18n[candiateLang].is_full_translation) {
                // There is a widget translation (and not just some names) for this language
                lang = candiateLang;
            }
            i++;
        }
        _txt.opts.pluralize = pluralizers[lang] || pluralizers[defaultLang];
        txt.keys = _txt.keys = i18n[lang] || i18nDefault;
        txt.currentLang = lang;
        widget.env.containerElm.lang = lang;
    };


    /**
     * Add translation keys
     * @param newKeys mapping language → key → translation
     * @param justFillGaps only fill gaps - don't overload anything that's already there
     * @param isFullTranslation This is a full widget translation and not just a few configured strings
     */
    const addKeys = function (newKeys, justFillGaps, isFullTranslation) {
        if (newKeys) {
            const updatedMarker = {};
            for (const lang in newKeys) {
                i18n[lang] = i18n[lang] || Object.create(i18nDefault);
                if (isFullTranslation) {
                    i18n[lang].is_full_translation = true;
                }
                for (const key in newKeys[lang]) {
                    const newKey = key.charAt(0) === '$' ? key : '$' + key;
                    if (!justFillGaps || !i18n[lang].hasOwnProperty(newKey)) {
                        i18n[lang][newKey] = newKeys[lang][key];
                        _txt.textHash = updatedMarker;
                    }
                }
            }
            _setTranslationKeys();
        }
    };

    const addApiDataName = function (key, name) {
        key = key.charAt(0) === '$' ? key : '$' + key;
        if (typeof name === 'string') {
            i18nDefault[key] = name;
        }
        else {
            // Treat name as a multi-language translation object.
            // i.e. { 'is':'Þýðing', 'en':'Translation', ... }
            for (const lang in name) {
                i18n[lang] = i18n[lang] || Object.create(i18nDefault);
                i18n[lang][key] = name[lang];
            }
        }
    };


    const __addStrings = function (lang, strings) {
        const i18nTemp = {};
        i18nTemp[lang] = strings;
        addKeys(i18nTemp, true, true);
        txt.isReady = true;
    };

    const _loadStrings = function (idx) {
        idx = idx || 0;
        const m = widget.m;
        const lang = desiredLangs[idx];
        if (lang && (lang !== defaultLang || !widget.defaultStrings)) {
            m.request({
                method: 'GET',
                url: widget.widget_url + 'i18n/' + lang + '.json',
                background: true,
                deserialize: data => data,
            })
                .then(
                    function (data) {
                        try {
                            __addStrings(lang, JSON.parse(data));
                            m.redraw();
                        } catch (e) {
                            widget.state.flagCatastrophe('i18n', 'Error loading language ' + lang, e.message);
                            _loadStrings(idx + 1);
                        }
                    },
                    function (/*errorResult*/) {
                        // The desired language wasn't found. Try next one.
                        _loadStrings(idx + 1);
                    }
                );
        }
        else {
            if (!widget.defaultStrings) {
                widget.state.flagCatastrophe('i18n', 'Default strings missing', desiredLangs);
            }
            __addStrings(defaultLang, widget.defaultStrings || {});
            m.redraw();
        }
    };

    const changeLang = function (lang) {
        desiredLangs[0] = lang;
        _loadStrings();
    };


    txt.addKeys = addKeys;
    txt.addApiDataName = addApiDataName;
    txt.start = _loadStrings;
    txt.changeLang = changeLang;
    txt.currentLang = null;
    txt.isReady = false;
    txt.textHash = {};
    // txt.pluralizers = pluralizers;

    return txt;
}
